/**
 * Web Push subscription registration.
 *
 * Call registerPushSubscription() once after the user is confirmed
 * logged in (e.g. after /auth/me succeeds).  It is idempotent —
 * calling it multiple times is safe.
 *
 * Key-rotation safety: we store the last-used VAPID public key in
 * localStorage.  If it has changed (or is missing) we unsubscribe the
 * stale browser subscription before creating a new one, so the backend
 * always receives a subscription that matches the current VAPID key.
 */
import API from './api';

const VAPID_KEY_STORAGE = 'vapid_pub_key';

/** Convert a URL-safe base64 string to Uint8Array (for applicationServerKey). */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

export async function registerPushSubscription() {
  try {
    // Feature-detect
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('[Push] serviceWorker or PushManager not supported');
      return;
    }

    // Must have permission or be able to ask
    if (Notification.permission === 'denied') {
      console.warn('[Push] Notification permission denied');
      return;
    }

    // Wait for the service worker to be ready
    console.log('[Push] Waiting for service worker ready...');
    const reg = await navigator.serviceWorker.ready;
    console.log('[Push] Service worker ready:', reg.active?.state);

    // Fetch VAPID public key from server — if not configured, bail silently
    let publicKey;
    try {
      const res = await API.get('/push/vapid-public-key');
      publicKey = res.data?.public_key;
      console.log('[Push] Got VAPID key from server');
    } catch (err) {
      console.warn('[Push] Failed to get VAPID key:', err?.response?.status, err?.message);
      return; // server not configured — push unavailable
    }
    if (!publicKey) { console.warn('[Push] No public key returned'); return; }

    // If the VAPID key changed since last subscription, drop the old one
    // (browser subscriptions are bound to a specific applicationServerKey)
    const storedKey = localStorage.getItem(VAPID_KEY_STORAGE);
    if (storedKey && storedKey !== publicKey) {
      const stale = await reg.pushManager.getSubscription();
      if (stale) await stale.unsubscribe().catch(() => {});
    }

    // Check for an existing valid subscription
    const existing = await reg.pushManager.getSubscription();
    let subscription = existing;
    console.log('[Push] Existing subscription:', existing ? 'yes' : 'none');

    if (!existing) {
      // Request permission if not yet granted
      if (Notification.permission === 'default') {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') { console.warn('[Push] Permission not granted:', perm); return; }
      }
      console.log('[Push] Subscribing...');
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      console.log('[Push] Subscribed successfully');
    }

    if (!subscription) return;

    // Remember which key this subscription uses
    localStorage.setItem(VAPID_KEY_STORAGE, publicKey);

    // Collect tokens for every account stored on this device so each
    // gets push notifications regardless of which one is currently active.
    // - Current session token (sessionStorage)
    // - Saved sibling student accounts (multi-account switcher)
    const json = subscription.toJSON();
    const tokens = new Set();
    const current = sessionStorage.getItem('access_token');
    if (current) tokens.add(current);
    try {
      const raw = localStorage.getItem('saved_student_accounts');
      if (raw) {
        const accounts = JSON.parse(raw);
        for (const a of accounts || []) {
          if (a?.token) tokens.add(a.token);
        }
      }
    } catch (_) { /* malformed storage — ignore */ }

    console.log(`[Push] Registering subscription for ${tokens.size} account(s)...`);
    await API.post('/push/subscribe-multi', {
      endpoint: json.endpoint,
      p256dh:   json.keys?.p256dh,
      auth:     json.keys?.auth,
      access_tokens: Array.from(tokens),
    });
    console.log('[Push] Subscription(s) registered with backend');
  } catch (err) {
    console.error('[Push] Registration failed:', err);
  }
}

/**
 * Unregister push for the currently-authenticated user.
 *
 * If there are still other saved accounts on this device (multi-account),
 * we ONLY remove the current user's row server-side and keep the browser
 * subscription alive — otherwise the siblings would lose notifications.
 * On the final logout (no siblings), we tear down the browser sub too.
 */
export async function unregisterPushSubscription() {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.ready;
    const subscription = await reg.pushManager.getSubscription();
    if (!subscription) return;
    const json = subscription.toJSON();

    // Server-side: remove this user's row for the current endpoint
    await API.post('/push/unsubscribe', {
      endpoint: json.endpoint,
      p256dh:   json.keys?.p256dh,
      auth:     json.keys?.auth,
    }).catch(() => {});

    // Are there other accounts left on this device?
    let hasOtherAccounts = false;
    try {
      const raw = localStorage.getItem('saved_student_accounts');
      if (raw) {
        const accounts = JSON.parse(raw) || [];
        const currentId = parseInt(localStorage.getItem('user_id') || '0', 10);
        hasOtherAccounts = accounts.some(a => a?.user_id && a.user_id !== currentId);
      }
    } catch (_) { /* ignore */ }

    if (!hasOtherAccounts) {
      await subscription.unsubscribe().catch(() => {});
      localStorage.removeItem(VAPID_KEY_STORAGE);
    }
  } catch (_) {}
}
